import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import './About.css';

const About: React.FC = () => {
  const { t } = useLanguage();
  const { about } = t;

  return (
    <div className="about">

      {/* ── Page Hero ──────────────────────────────────────── */}
      <section className="page-hero page-hero--about">
        <div className="page-hero__overlay" />
        <div className="page-hero__content container">
          <div className="section-label section-label--light">{about.pageLabel}</div>
          <h1 className="page-hero__headline">
            {about.heroHeadline} <span className="text-accent">{about.heroHeadlineAccent}</span>
          </h1>
          <p className="page-hero__sub">{about.heroSub1}</p>
          <p className="page-hero__sub">{about.heroSub2}</p>
        </div>
      </section>

      {/* ── Story & Mission ────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="about-story__grid">
            <div className="about-story__content">
              <div className="section-label">{about.storyLabel}</div>
              <h2 className="section-title">
                {about.storyTitle}<br />
                <span className="text-accent">{about.storyTitleAccent}</span>
              </h2>
              <p className="section-body">{about.storyBody1}</p>
              <p className="section-body">{about.storyBody2}</p>
              <p className="section-body">{about.storyBody3}</p>
            </div>
            <div className="about-story__visual">
              <div className="about-story__mission-card">
                <div className="about-story__mission-icon">🎯</div>
                <h3>{about.missionTitle}</h3>
                <p>{about.missionBody}</p>
              </div>
              <div className="about-story__vision-card">
                <div className="about-story__mission-icon">🔭</div>
                <h3>{about.visionTitle}</h3>
                <p>{about.visionBody}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Our Approach ───────────────────────────────────── */}
      <section className="section section--alt">
        <div className="container">
          <div className="section-header">
            <div className="section-label">{about.approachLabel}</div>
            <h2 className="section-title">{about.approachTitle}</h2>
            <p className="section-subtitle">{about.approachSub}</p>
          </div>
          <div className="about-approach__grid">
            {about.approaches.map((item, i) => (
              <div className="about-approach__card" key={i}>
                <div className="about-approach__card-number">0{i + 1}</div>
                <div className="about-approach__card-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Our Expertise ──────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <div className="section-label">{about.expertiseLabel}</div>
            <h2 className="section-title">
              {about.expertiseTitle} <span className="text-accent">{about.expertiseTitleAccent}</span>
            </h2>
            <p className="section-subtitle">{about.expertiseSub}</p>
          </div>
          <div className="about-expertise__grid">
            {about.expertise.map((item, i) => (
              <div className="about-expertise__card" key={i}>
                <div className="about-expertise__icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── G-Medix Current Project ────────────────────────── */}
      <section className="section section--dark">
        <div className="container">
          <div className="section-label section-label--light">{about.gmedixLabel}</div>
          <div className="gmedix-detail__grid">
            <div>
              <h2 className="section-title section-title--light" style={{ marginBottom: '1.5rem' }}>
                {about.gmedixTitle}
              </h2>
              <p className="section-body section-body--light">{about.gmedixBody1}</p>
              <p className="section-body section-body--light">{about.gmedixBody2}</p>
              <p className="section-body section-body--light">{about.gmedixBody3}</p>
            </div>
            <div className="gmedix-detail__phases">
              <h3>{about.gmedixPhasesTitle}</h3>
              <div className="gmedix-detail__phase">
                <div className="gmedix-detail__phase-header">
                  <span className="gmedix-detail__phase-badge">{about.gmedixPhase1Label}</span>
                  <span>{about.gmedixPhase1Title}</span>
                </div>
                <p>{about.gmedixPhase1Body}</p>
              </div>
              <div className="gmedix-detail__phase">
                <div className="gmedix-detail__phase-header">
                  <span className="gmedix-detail__phase-badge">{about.gmedixPhase2Label}</span>
                  <span>{about.gmedixPhase2Title}</span>
                </div>
                <p>{about.gmedixPhase2Body}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Social Impact ──────────────────────────────────── */}
      <section className="section section--alt">
        <div className="container">
          <div className="section-header">
            <div className="section-label">{about.impactLabel}</div>
            <h2 className="section-title">
              {about.impactTitle}<br />
              <span className="text-accent">{about.impactTitleAccent}</span>
            </h2>
          </div>
          <div className="about-impact__grid">
            <div className="about-impact__content">
              <h3>{about.impactCommitmentTitle}</h3>
              <p>{about.impactCommitmentBody}</p>
              <h3>{about.impactInitTitle}</h3>
              <p>{about.impactInitBody}</p>
              <h3>{about.impactPartnerTitle}</h3>
              <p>{about.impactPartnerBody}</p>
              <a href="mailto:socialimpact@mahmedix.com" className="btn btn--secondary about-impact__btn">
                socialimpact@mahmedix.com
              </a>
            </div>
            <div className="about-impact__vision">
              <div className="about-impact__vision-card">
                <div className="about-impact__vision-icon">🌍</div>
                <blockquote>
                  "{about.impactQuote}"
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────── */}
      <section className="cta-banner">
        <div className="cta-banner__overlay" />
        <div className="container cta-banner__content">
          <h2 className="cta-banner__headline">{about.ctaTitle}</h2>
          <p className="cta-banner__body">{about.ctaBody}</p>
          <Link to="/contact" className="btn btn--secondary btn--lg">
            {about.ctaBtn}
          </Link>
        </div>
      </section>

    </div>
  );
};

export default About;
